<template>
  <div class="accountants-signup-form">
    <h2 class="text-campton-bold text-accountants">{{ $gettext('Signup as an accountant') }}</h2>
    <b-form @submit.prevent="submitSignup" class="gk-clear-form">
      <CountrySelector v-model="businesslocation" :accepted="!loadingStepTuah && signupStep === 2" />

      <div>
        <b-form-group
          id="fieldset-signup-companyname"
          class="form-group floating-label"
          :class="{ accepted: !loadingStepTuah && signupStep === 2 }"
        >
          <b-form-input
            type="text"
            id="input-signup-companyname"
            placeholder=" "
            class="floating-label"
            :class="{ 'is-touch': companynameTouch }"
            v-model="companyname"
            :state="companynameState"
            trim
            required
            @blur="companynameTouch = true"
            :disabled="!loadingStepTuah && signupStep === 2"
          />
          <label class="floating-label">{{ $gettext('Accountant company name') }}</label>
          <div class="input-check"><font-awesome-icon icon="fa-solid fa-check" /></div>
        </b-form-group>
      </div>

      <div>
        <b-form-group
          id="fieldset-signup-username"
          class="form-group floating-label"
          :class="{ accepted: !loadingStepTuah && signupStep === 2 }"
        >
          <b-form-input
            type="text"
            id="input-signup-username"
            placeholder=" "
            class="floating-label"
            :class="{ 'is-touch': usernameTouch }"
            v-model="username"
            :state="usernameState"
            trim
            required
            @blur="usernameTouch = true"
            :disabled="!loadingStepTuah && signupStep === 2"
          />
          <label class="floating-label">{{ $gettext('Your full name') }}</label>
          <div class="input-check"><font-awesome-icon icon="fa-solid fa-check" /></div>
        </b-form-group>
      </div>

      <div>
        <b-form-group
          id="fieldset-signup-email"
          class="form-group floating-label"
          :class="{ accepted: !loadingStepTuah && signupStep === 2 }"
        >
          <b-form-input
            type="text"
            id="input-signup-email"
            placeholder=" "
            class="floating-label"
            :class="{ 'is-touch': emailTouch }"
            v-model="email"
            :state="emailState"
            trim
            required
            @blur="emailTouch = true"
            :disabled="!loadingStepTuah && signupStep === 2"
          />
          <label class="floating-label">{{ $gettext('Email address') }}</label>
          <div class="input-check"><font-awesome-icon icon="fa-solid fa-check" /></div>
          <small v-if="showEmailWarning || (emailTouch && !emailState)" class="email-hint form-text">
            <transition name="fade" appear mode="out-in">
              <span v-if="showEmailWarning" :key="showEmailWarning" class="text-danger">
                <font-awesome-icon icon="fa-regular fa-warning" />{{ emailWarningTxt }}
              </span>
              <span v-else-if="emailTouch && !emailState" :key="showEmailWarning" class="text-danger">
                <font-awesome-icon icon="fa-regular fa-circle-xmark" />{{ $gettext('Please enter a valid email') }}
              </span>
            </transition>
          </small>
        </b-form-group>
      </div>

      <div>
        <b-form-group
          id="fieldset-signup-phone"
          class="form-group floating-label"
          :class="{ accepted: !loadingStepTuah && signupStep === 2 }"
        >
          <b-form-input
            type="text"
            id="input-signup-phone"
            placeholder=" "
            class="floating-label"
            :class="{ 'is-touch': phoneTouch }"
            v-model="phone"
            :state="phoneState"
            trim
            required
            @blur="phoneTouch = true"
            :disabled="!loadingStepTuah && signupStep === 2"
          />
          <label class="floating-label">{{ $gettext('Phone number') }}</label>
          <div class="input-check"><font-awesome-icon icon="fa-solid fa-check" /></div>
        </b-form-group>
      </div>

      <div class="step-tuah" :class="{ expanded: !loadingStepTuah && signupStep === 2 }">
        <div class="step-tuah--branding">
          <h3>{{ $gettext('Branding options') }}</h3>

          <b-form-group label-cols="6" label-size="sm" :label="$gettext('Company color*')" label-for="input-sm">
            <b-form-input type="color" class="input-color" size="sm" v-model="companycolor" />
            <span class="picker-hex" :style="{ color: getHighContrastColor(companycolor) }">{{ companycolor }}</span>
          </b-form-group>

          <b-form-group id="fieldset-companylogo" :label="$gettext('Company logo*')" label-for="input-companylogo">
            <div class="logo-uploader">
              <VueDropzone
                ref="logo-uploader"
                allowed-files="img"
                only-change
                disable-download
                disable-edit
                :zoomable="false"
                :preloaded_file="defaultLogo"
                @change="fileChanged"
              />

              <div class="logo-uploader--hint" @click="$refs['logo-uploader'].openFileDialog()">
                <font-awesome-icon icon="fa-solid fa-upload" />
                <span v-if="isDefaultLogo">{{ $gettext('Click to add your logo') }}</span>
                <span v-else>{{ $gettext('Click to replace your logo') }}</span>
              </div>
            </div>
          </b-form-group>

          <p v-if="showLogoWarning" class="text-danger text-center">
            <font-awesome-icon icon="fa-regular fa-warning" />
            {{ $gettext('Please upload your company logo.') }}
          </p>

          <div class="topbar-preview-wrap">
            <small class="form-text">{{
              $gettext('Your customers will see the top bar in Gekko as shown below')
            }}</small>
            <div class="topbar-preview" :style="'background-color: ' + companycolor + ';'">
              <div class="topbar-preview__avatar">
                <img v-if="(companylogo && logoURL) || defaultLogo" :src="logoURL || defaultLogo" alt="" />
              </div>
              <div class="topbar-preview__company">
                <div class="topbar-preview__company--name">{{ $gettext("Customer's company") }}</div>
                <div class="topbar-preview__company--customer">{{ $gettext("Customer's name") }}</div>
              </div>
              <div class="topbar-preview__tabs">
                <div class="topbar-preview__tabs--tab active">
                  <font-awesome-icon icon="fa-solid fa-house" />
                  {{ $gettext('Dashboard') }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <b-form-group id="fieldset-signup-password" class="form-group floating-label">
          <div class="position-relative">
            <b-form-input
              type="password"
              id="input-signup-password"
              placeholder=" "
              class="floating-label"
              :class="{ 'is-touch': passwordTouch }"
              v-model="password"
              :state="passwordState"
              trim
              :required="!loadingStepTuah && signupStep === 2"
              :disabled="signupStep === 1"
              @blur="passwordTouch = true"
            />
            <label class="floating-label">{{ $gettext('Password') }}</label>
            <ShowPasswordIcon input-id="input-signup-password" />
          </div>
          <PasswordStrengthHint
            v-if="showPasswordHint"
            :password="password"
            @score-changed="(score) => {passwordScore = score}"
            @password-length-exceeded="(score) => { passwordScore = score }"
          />
        </b-form-group>
      </div>
      <div>
        <GekkoButton
          @click="signupStep = 1"
          :text="$gettext('Previous step')"
          variant="link"
          :disabled="signupStep === 1"
          :style="'opacity: ' + (signupStep === 1 ? 0 : 1) + ' !important;'"
          class=""
        />
        <GekkoButton
          type="submit"
          :text="signupStep === 1 ? $gettext('Get started') : $gettext('And you are done!')"
          :loading="loadingStepTuah || savingData"
          :disabled="signupStep === 2 && passwordScore < 1"
          variant="accountants"
          class="w-100 text-campton-book position-relative"
          size="xl"
        />
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import { randomCelebName, randomEmailAccountant, getHighContrastColor } from '@/utils/'

import GekkoButton from '@/components/GekkoButton'
import ShowPasswordIcon from '@/components/ui/ShowPasswordIcon'
import PasswordStrengthHint from '@/components/ui/PasswordStrengthHint'
import VueDropzone from '@/components/VueDropzone'
import CountrySelector from '@/components/signup/CountrySelector'

export default {
  name: 'SignupFormAccountants',
  components: {
    GekkoButton,
    ShowPasswordIcon,
    PasswordStrengthHint,
    VueDropzone,
    CountrySelector,
  },
  data() {
    return {
      companyname: '',
      username: '',
      email: '',
      phone: '',
      companycolor: '#22AC8A',
      companylogo: null,
      preferredlanguage: '',
      password: '',

      showEmailWarning: false,
      emailWarningTxt: '',

      showLogoWarning: false,

      passwordScore: 0,
      signupStep: 1,
      loading: false,
      loadingUserLocation: false,
      savingData: false,
      businesslocation: '',

      companynameTouch: false,
      usernameTouch: false,
      emailTouch: false,
      phoneTouch: false,
      passwordTouch: false,
      defaultLogo: '/static/img/gk-accountants-default-logo.png',
    }
  },
  computed: {
    ...mapGetters({
      languageOptions: 'signup/getLanguageOptions',
      userLanguage: 'signup/getUserLanguage',
      userCountry: 'signup/getUserCountry',
    }),
    randomPlaceholderName() {
      return randomCelebName()
    },
    randomPlaceholderEmail() {
      return randomEmailAccountant(this.randomPlaceholderName)
    },
    loadingStepTuah() {
      return this.loading || this.loadingLanguages || this.loadingUserLocation
    },
    logoURL() {
      let logo_url = null
      if (this.companylogo) {
        logo_url = URL.createObjectURL(this.companylogo)
      }
      return logo_url
    },
    companynameState() {
      return true
    },
    usernameState() {
      return true
    },
    phoneState() {
      return true
    },
    emailState() {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      return emailRegex.test(this.email) && !this.showEmailWarning
    },
    showPasswordHint() {
      return this.password.length > 0
    },
    passwordState() {
      return this.passwordScore >= 1
    },
    isDefaultLogo() {
      return !this.companylogo && !this.logoURL
    },
  },
  created() {
    this.setupData()
  },
  mounted() {},
  methods: {
    getHighContrastColor,
    ...mapActions({
      saveData: 'signup/signUpAccountants',
      checkAvailability: 'signup/checkAvailability',
      geoLocateUserIp: 'signup/geoLocateUserIp',
      getAccessToken: 'auth/getAccessToken',
    }),
    setupData() {
      console.log('this.$route.params.language', this.$route.params.language)
      // geo locate user
      this.loadingUserLocation = true
      this.geoLocateUserIp().then(() => {
        this.preferredlanguage = this.userLanguage || this.$route.params.language
        this.businesslocation = this.userCountry
        this.loadingUserLocation = false
      })

      // set gekko logo as company logo by default
      // this.companylogo = '/static/images/gekko.png'
    },
    submitSignup() {
      if (this.signupStep === 1) {
        this.checkEmailAvailability()
      }
      if (this.signupStep === 2 && this.checkFormValidity()) {
        const data = {
          company_name: this.companyname,
          full_name: this.username,
          email: this.email,
          password: this.password,
          preferred_language: this.preferredlanguage,
          phone_number: this.phone,
          logo: this.companylogo,
          color: this.companycolor,
          country: this.businesslocation,
        }
        this.savingData = true
        this.saveData(data)
          .then(() => {
            console.log('saved data, trying login')
            this.login()
          })
          .catch((error) => {
            console.log('error', error)
          })
          .finally(() => {
            this.savingData = false
          })
      }
    },
    checkFormValidity() {
      let is_valid = true
      if (this.passwordScore < 1) {
        is_valid = false
      }
      if (!this.companylogo) {
        is_valid = false
        this.showLogoWarning = true
      }
      return is_valid
    },
    fileChanged(file) {
      if (file) {
        this.showLogoWarning = false
      }
      this.companylogo = file
    },
    checkEmailAvailability() {
      this.loading = true
      this.checkAvailability(this.email)
        .then((response) => {
          this.showEmailWarning = false
          console.log('response', response)
          if (response.status === 200) {
            this.signupStep = 2
          }
        })
        .catch((error) => {
          if (error.status === 409) {
            this.showEmailWarning = true
            this.emailWarningTxt = this.$gettext('This email address already exists')
          } else if (error.status === 422) {
            this.showEmailWarning = true
            this.emailWarningTxt = this.$gettext("That email doesn't seem to be ok")
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    login() {
      this.loading = true
      this.getAccessToken({
        username: this.email,
        password: this.password,
      })
        .then((response) => {
          console.log('Login.vue::login() -> getAccessToken THEN response', response)
          this.$emit('authorised', false)
        })
        .catch((error) => {
          console.log('error authenticating new user', error)
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss">
.accountants-signup-form {
  #dropzonePreview {
    border-radius: 50em;
    border: 1px solid;
    overflow: hidden;
    width: 175px;
    height: 175px;
  }

  .email-hint {
    span {
      .svg-inline--fa {
        margin-right: 0.25rem;
      }
    }
  }
  .logo-uploader {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    .gk-vue-dropzone { display: flex; }
    &--hint {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      align-items: center;
      justify-content: center;
      flex: 1;
      color: $colorBlue;
      cursor: pointer;
      svg {
        font-size: 26px;
      }
      span {
        font-size: 1rem;
      }
    }
  }
  .form-control {
    &:not(.is-touch) {
      &.is-invalid,
      &.is-valid,
      &.is-invalid:focus,
      &.is-valid:focus {
        border: 1px solid #dfdfdf;
      }
    }
    &.is-touch {
      &.is-invalid {
        border: 1px solid $colorDanger;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.step-tuah {
  max-height: 0;
  opacity: 0;
  transition: 0.7s ease;
  overflow: hidden;
  &.expanded {
    opacity: 1;
    max-height: 700px;
    padding-top: 2rem;
  }
  &--branding {
    border-radius: $radiusMd;
    border: 1px solid $colorBorderDash;
    padding: 0 1rem;
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    h3 {
      background: white;
      margin-top: -1rem;
      margin-left: -0.25rem;
      margin-bottom: 1rem;
      padding: 0 0.25rem;
      display: flex;
      width: fit-content;
      border-radius: $radiusMd;
      font-size: 22px;
    }
    .input-color {
      height: 37px !important;
    }
    .picker-hex {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 14px;
      pointer-events: none;
    }
    .topbar-preview-wrap {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      width: 100%;
      padding: 1rem 0;
      .topbar-preview {
        display: flex;
        gap: 0.5rem;
        align-items: center;
        width: 100%;
        height: 50px;
        padding: 0.25rem 0.5rem;
        color: white;
        border-radius: $radiusMd;
        &__avatar {
          border-radius: 50em;
          overflow: hidden;
          width: 40px;
          height: 40px;
          background: white;
          position: relative;
          flex-shrink: 0;
          img {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            object-fit: cover;
          }
        }
        &__company {
          display: flex;
          flex-direction: column;
          justify-content: center;
          height: 100%;
          color: white;
          font-family: DINNextLTPro-Regular;
          &--name {
            font-size: 26px;
            white-space: nowrap;
            line-height: 1;
            display: block;
            font-family: DINNextLTPro-Medium;
          }
          &--customer {
            font-size: 12px;
            opacity: 0.5;
          }
        }
        &__tabs {
          display: flex;
          gap: 1rem;
          padding-top: 0.25rem;
          flex: 1;
          justify-content: flex-end;
          align-items: center;
          &--tab {
            display: flex;
            flex-direction: column;
            font-size: 12px;
            opacity: 0.7;
            text-align: center;
            .svg-inline--fa {
              font-size: 20px;
            }
            &.active {
              opacity: 1;
            }
          }
        }
      }
    }
  }
}
</style>